'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { ShoppingCartIcon, Trash2Icon, CreditCardIcon } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PriceDisplay } from '@/components/partials';
import CartItemCard from './cart-item-card';
import CartContentSkeleton from './cart-content-skeleton';
import CheckoutDialog from './checkout-dialog';
import GuestCartContent from './guest-cart-content';
import { useAppLocale, useSession, useTranslatedRoute } from '@/hooks';
import { getErrorMessage } from '@/lib/utils';
import { useCartStore } from '@/store';
const CartPageContent = () => {
  const t = useTranslations();
  const {
    isAuthenticated,
    sessionIsLoading
  } = useSession();
  const {
    cartState,
    getCart,
    clearCart
  } = useCartStore();
  const {
    backendLocale
  } = useAppLocale();
  const {
    buildRoute
  } = useTranslatedRoute();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    getCart().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  const {
    isLoading,
    error,
    response
  } = cartState;
  const items = response?.items ?? [];
  const totalItems = response?.totalItems ?? 0;
  const totalQuantity = response?.totalQuantity ?? 0;
  const total = response?.total;
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (searchParams.get('checkout') === '1' && items.length > 0 && !checkoutOpen) {
      setCheckoutOpen(true);
      router.replace(pathname, {
        scroll: false
      });
    }
  }, [isAuthenticated, searchParams, items.length, checkoutOpen, router, pathname]);
  if (sessionIsLoading) {
    return <CartContentSkeleton />;
  }
  if (!isAuthenticated) {
    return <GuestCartContent />;
  }
  const handleClearCart = async () => {
    setClearing(true);
    try {
      await clearCart();
      toast.success(t('cart_cleared_success'));
      setClearOpen(false);
    } catch (error: any) {
      toast.error(getErrorMessage(error, t('something_went_wrong')));
    } finally {
      setClearing(false);
    }
  };
  return <div className="flex w-full flex-col gap-6 rounded-lg bg-white p-6 shadow-sm">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900">
          {t('shopping_cart')}
          {totalItems > 0 && <span className="text-muted-foreground ml-2 text-sm font-normal">
              {t('cart_summary', {
            items: totalItems,
            quantity: totalQuantity
          })}
            </span>}
        </h2>
        {items.length > 0 && <Button variant="outline" className="text-destructive border-destructive/30 hover:border-destructive hover:text-destructive text-xs" onClick={() => setClearOpen(true)}>
            <Trash2Icon className="mr-1 h-3.5 w-3.5" />
            {t('clear_cart')}
          </Button>}
      </div>

      <Separator />

      {/* Loading — only on initial fetch */}
      {isLoading && !response && <CartContentSkeleton />}

      {/* Error */}
      {error && !isLoading && !response && <div className="flex flex-col items-center gap-3 py-10">
          <p className="text-destructive text-sm">{t('something_went_wrong')}</p>
          <Button variant="outline" size="sm" onClick={() => getCart().catch(() => {})}>
            {t('try_again')}
          </Button>
        </div>}

      {/* Empty */}
      {!isLoading && !error && items.length === 0 && response !== null && <div className="flex flex-col items-center gap-3 py-12 text-center">
          <ShoppingCartIcon className="text-muted-foreground h-12 w-12" />
          <p className="text-muted-foreground text-sm">{t('cart_is_empty')}</p>
          <Button variant="outline" asChild>
            <Link href={buildRoute('/products')}>{t('continue_shopping')}</Link>
          </Button>
        </div>}

      {/* Cart items + total */}
      {items.length > 0 && <div className="flex flex-col gap-3">
          {items.map(item => <CartItemCard key={item.id} item={item} />)}
          {total && <>
              <Separator />
              <div className="flex flex-col gap-1.5 px-1">
                {total.originalGross !== null && total.originalGross !== undefined && total.originalGross !== total.gross && <>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground text-xs">{t('subtotal')}</span>
                        <span className="text-xs text-gray-600">
                          {new Intl.NumberFormat(backendLocale, {
                  style: 'currency',
                  currency: total.currencyCode,
                  minimumFractionDigits: 2
                }).format(total.originalGross)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-green-700">{t('discount')}</span>
                        <span className="text-xs font-medium text-green-700">
                          -
                          {new Intl.NumberFormat(backendLocale, {
                  style: 'currency',
                  currency: total.currencyCode,
                  minimumFractionDigits: 2
                }).format(total.originalGross - total.gross)}
                        </span>
                      </div>
                    </>}
                {total.taxAmount > 0 && <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-xs">{t('tax')}</span>
                    <span className="text-xs text-gray-600">
                      {new Intl.NumberFormat(backendLocale, {
                style: 'currency',
                currency: total.currencyCode,
                minimumFractionDigits: 2
              }).format(total.taxAmount)}
                    </span>
                  </div>}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">{t('total')}</span>
                  <PriceDisplay gross={total.gross} originalGross={total.originalGross} discountRate={total.discountRate} currencyCode={total.currencyCode} locale={backendLocale} size="md" discountedClassName="text-base" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button size="default" className="gap-2 bg-green-600 text-white hover:bg-green-700" onClick={() => setCheckoutOpen(true)}>
                  <CreditCardIcon className="h-4 w-4" />
                  {t('checkout')}
                </Button>
              </div>
            </>}
        </div>}

      {/* Clear cart confirm dialog */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('clear_cart')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(90vh-9rem)]">
            <div className="px-5 pr-3 pb-4">
              <p className="text-muted-foreground text-sm">{t('confirm_clear_cart')}</p>
            </div>
          </ScrollArea>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setClearOpen(false)} disabled={clearing}>
              {t('cancel')}
            </Button>
            <Button variant="destructive" onClick={handleClearCart} disabled={clearing}>
              {t('clear_cart')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </div>;
};
export default CartPageContent;